import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import { HorizontalCarousel, SizingMultiplier, Typography } from '@ocx/ui';

import { OfferLocationCoordinates } from '../offer-location-coordinates.types';
import { messages } from './offer-details.messages';
import { OfferLocationsCarouselItem } from './offer-locations-carousel-item.component';
import { OfferLocationsCarouselItemOnClickParams } from './offer-details-locations.types';

export type OfferDetailsAvailableAtLocation = {
  uuid: string;
  name: string;
  address: string;
  coordinates: OfferLocationCoordinates;
};

export type OfferDetailsLocationsCarouselProps = {
  locations?: OfferDetailsAvailableAtLocation[];
  // Indicates if the offer has location restrictions.
  // The `locations` array lists available locations, but restrictions may still apply (e.g., "Location excludes" in Command Center can make this array empty).
  hasLocationConstraints?: boolean;
  memberHasSharedLocation?: boolean;
  onStoreIconClick?: (params: OfferLocationsCarouselItemOnClickParams) => void;
};

export const OfferDetailsLocationsCarousel = ({
  locations,
  hasLocationConstraints,
  memberHasSharedLocation,
  onStoreIconClick,
}: OfferDetailsLocationsCarouselProps) => {
  const intl = useIntl();
  let title = '';

  if (!hasLocationConstraints) {
    title = intl.formatMessage(messages['offer-details:location-constraint:title:available-at-all-stores']);
  } else if (hasLocationConstraints && !memberHasSharedLocation) {
    title = intl.formatMessage(
      messages['offer-details:location-constraint:title:may-not-be-available-at-nearby-stores'],
    );
  } else if (hasLocationConstraints && memberHasSharedLocation && locations?.length) {
    title = intl.formatMessage(messages['offer-details:location-constraint:title:available-at-these-nearby-stores']);
  }

  if (!title && !locations?.length) {
    return null;
  }

  return (
    <Box borderRadius={SizingMultiplier.md} p={SizingMultiplier.md} bgcolor="background.box">
      {title ? (
        <Typography variant="body2" mb={locations?.length ? SizingMultiplier.md : 0}>
          {title}
        </Typography>
      ) : undefined}

      {locations?.length ? (
        <HorizontalCarousel spacing={SizingMultiplier.sm} edgeSpacing={SizingMultiplier.md}>
          {locations.map(({ uuid, name, address, coordinates }) => (
            <OfferLocationsCarouselItem
              key={uuid}
              uuid={uuid}
              name={name}
              address={address}
              coordinates={coordinates}
              onClick={onStoreIconClick}
            />
          ))}
        </HorizontalCarousel>
      ) : undefined}
    </Box>
  );
};
